/**
 * PushToggle — "Receive a gentle nudge on this device" switch.
 * Calls /api/push/* via lib/push.js. Calm UX, never alarms the user about
 * permissions — only invites them in.
 */
import { useEffect, useState } from "react";
import { Bell, BellOff } from "lucide-react";
import { toast } from "sonner";
import { disablePush, enablePush, getPushState } from "../lib/push";

export default function PushToggle({ testid = "push-toggle" }) {
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState(false);

  const refresh = async () => setState(await getPushState());

  useEffect(() => {
    refresh();
  }, []);

  if (!state) return null;

  if (!state.supported) {
    return (
      <p
        data-testid={`${testid}-unsupported`}
        className="text-xs text-[#525252] italic"
      >
        Your browser doesn&rsquo;t support push on this device. We&rsquo;ll
        keep nudges in the app.
      </p>
    );
  }

  const subscribed = state.subscribed && state.permission === "granted";
  const onClick = async () => {
    setBusy(true);
    try {
      if (subscribed) {
        await disablePush();
        toast("Notifications gently paused.");
      } else {
        await enablePush();
        toast("This device will receive calm nudges.");
      }
      await refresh();
    } catch (err) {
      toast(err.message || "Couldn't update.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid={testid}
      className="flex items-center justify-between gap-3"
    >
      <span
        data-testid={`${testid}-status`}
        className={
          "text-sm font-medium " +
          (subscribed ? "text-[#1A1B2E]" : "text-[#525252]")
        }
      >
        {subscribed ? "On for this device" : "Off"}
        {state.permission === "denied" && (
          <span className="block text-[10px] tracking-[0.18em] uppercase text-[#9C7826] mt-1">
            Browser blocked notifications — enable in site settings
          </span>
        )}
      </span>
      <button
        type="button"
        onClick={onClick}
        disabled={busy || state.permission === "denied"}
        data-testid={`${testid}-button`}
        className={
          "inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs tracking-[0.16em] uppercase font-medium transition-colors disabled:opacity-50 " +
          (subscribed
            ? "bg-[#1F3A2A]/15 text-[#1F3A2A] hover:bg-[#1F3A2A]/25"
            : "bg-[#132619] text-white hover:bg-[#0b2117]")
        }
      >
        {subscribed ? (
          <>
            <BellOff size={13} strokeWidth={1.7} />
            Mute on this device
          </>
        ) : (
          <>
            <Bell size={13} strokeWidth={1.7} />
            Enable on this device
          </>
        )}
      </button>
    </div>
  );
}
